(() => {
  'use strict';
  const root = window.Shopify?.routes?.root || '/';
  const strings = window.gpcTheme?.strings || {};
  const translate = (key, values = {}) => (strings[key] || key).replace(/__GPC_(\w+)__/g, (match, name) => values[name] ?? match);
  const languageMenus = [...document.querySelectorAll('[data-gpc-language-menu]')];
  languageMenus.forEach(menu => {
    const trigger = menu.querySelector('summary');
    const choices = [...menu.querySelectorAll('[name="language_code"]')];
    const closeMenu = (restoreFocus = false) => {
      menu.open = false;
      if (restoreFocus) trigger.focus();
    };
    menu.addEventListener('toggle', () => {
      if (menu.open) languageMenus.forEach(other => { if (other !== menu) other.open = false; });
    });
    menu.addEventListener('keydown', event => {
      if (event.key === 'Escape' && menu.open) {
        event.preventDefault();
        event.stopPropagation();
        closeMenu(true);
      } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        menu.open = true;
        const current = choices.indexOf(document.activeElement);
        const next = current < 0 ? (event.key === 'ArrowDown' ? 0 : choices.length - 1) : (current + (event.key === 'ArrowDown' ? 1 : -1) + choices.length) % choices.length;
        choices[next]?.focus();
      }
    });
    menu.addEventListener('focusout', () => {
      requestAnimationFrame(() => { if (!menu.contains(document.activeElement)) closeMenu(); });
    });
    menu.closest('form').addEventListener('submit', event => {
      if (event.submitter?.getAttribute('aria-current') === 'true') {
        event.preventDefault();
        closeMenu(true);
        return;
      }
      const returnTo = event.currentTarget.querySelector('[name="return_to"]');
      if (returnTo) returnTo.value = location.pathname + location.search + location.hash;
    });
  });
  document.addEventListener('pointerdown', event => {
    languageMenus.forEach(menu => { if (menu.open && !menu.contains(event.target)) menu.open = false; });
  });
  const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  let userPaused = false;
  try { userPaused = localStorage.getItem('gpc-motion-paused') === 'true'; } catch (_) {}
  const applyMotion = () => {
    const paused = userPaused || motionQuery.matches;
    document.documentElement.classList.toggle('gpc-motion-off', paused);
    document.querySelectorAll('[data-gpc-motion]').forEach(button => {
      button.setAttribute('aria-pressed', String(paused));
      button.textContent = paused ? translate('ui.motion_paused') : translate('ui.pause_motion');
      button.disabled = motionQuery.matches;
    });
    if (paused) document.querySelectorAll('.gpc-reveal-pending').forEach(el => el.classList.remove('gpc-reveal-pending'));
  };
  document.addEventListener('click', event => {
    if (!event.target.closest('[data-gpc-motion]')) return;
    userPaused = !userPaused;
    try { localStorage.setItem('gpc-motion-paused', String(userPaused)); } catch (_) {}
    applyMotion();
  });
  motionQuery.addEventListener('change', applyMotion);

  // The section keeps its original space; only the visible header contracts.
  // This avoids moving page content or the sticky gallery when crossing the threshold.
  let headerFrame = 0;
  const updateHeader = () => {
    headerFrame = 0;
    const compact = document.documentElement.classList.contains('gpc-header-compact');
    if (scrollY > 90 && !compact) document.documentElement.classList.add('gpc-header-compact');
    else if (scrollY < 25 && compact) document.documentElement.classList.remove('gpc-header-compact');
  };
  window.addEventListener('scroll', () => { if (!headerFrame) headerFrame = requestAnimationFrame(updateHeader); }, { passive: true });
  window.addEventListener('pageshow', updateHeader);
  updateHeader();

  let revealObserver;
  let sceneObserver;
  const activeScenes = new Set();
  let ticking = false;
  const updateParallax = () => {
    ticking = false;
    if (userPaused || motionQuery.matches) return;
    activeScenes.forEach(el => {
      const bounds = el.getBoundingClientRect();
      const progress = Math.max(0, Math.min(1, (window.innerHeight - bounds.top) / (window.innerHeight + bounds.height)));
      el.style.setProperty('--gpc-progress', progress.toFixed(3));
    });
  };
  const scheduleParallax = () => { if (!ticking) { ticking = true; requestAnimationFrame(updateParallax); } };
  if ('IntersectionObserver' in window) {
    revealObserver = new IntersectionObserver(entries => entries.forEach(entry => {
      if (entry.isIntersecting) { entry.target.classList.remove('gpc-reveal-pending'); revealObserver.unobserve(entry.target); }
    }), { threshold: .08, rootMargin: '0px 0px -30px 0px' });
    sceneObserver = new IntersectionObserver(entries => entries.forEach(entry => {
      entry.target.querySelectorAll('.gpc-smoke > div').forEach(layer => { layer.style.animationPlayState = entry.isIntersecting ? 'running' : 'paused'; });
      if (entry.target.matches('[data-gpc-parallax]')) {
        if (entry.isIntersecting) activeScenes.add(entry.target); else activeScenes.delete(entry.target);
        scheduleParallax();
      }
    }), { rootMargin: '120px' });
    window.addEventListener('scroll', scheduleParallax, { passive: true });
    window.addEventListener('resize', scheduleParallax, { passive: true });
  }
  function initMotion(scope = document) {
    scope.querySelectorAll('[data-gpc-reveal]').forEach(el => {
      if (el.dataset.gpcRevealReady) return;
      el.dataset.gpcRevealReady = 'true';
      if (revealObserver && !motionQuery.matches && !userPaused && !window.Shopify?.designMode) {
        if (el.getBoundingClientRect().top > window.innerHeight * .85) el.classList.add('gpc-reveal-pending');
        revealObserver.observe(el);
      }
    });
    scope.querySelectorAll('[data-gpc-parallax], .gpc-cta, .gpc-gallery__stage').forEach(el => sceneObserver?.observe(el));
    applyMotion();
  }

  const safeText = (tag, text, className) => {
    const el = document.createElement(tag); el.textContent = text;
    if (className) el.className = className;
    return el;
  };
  const formatMoney = (cents, currency) => new Intl.NumberFormat('en-AE', { style: 'currency', currency, currencyDisplay: 'code' }).format(cents / 100);
  let cartOpener;
  let cartRequest = 0;
  let cartChange = null;
  const setCounts = count => {
    document.querySelectorAll('[data-gpc-count]').forEach(el => { el.textContent = count; el.hidden = count === 0 && Boolean(el.closest('.gpc-cart-link')); });
    document.querySelectorAll('[data-gpc-cart-open]').forEach(el => el.setAttribute('aria-label', translate('cart.open', {count})));
  };
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, event => { if (event.cartData) setCounts(event.cartData.item_count); });
  }
  function renderCart(cart, dialog) {
    const itemsEl = dialog.querySelector('[data-gpc-cart-items]');
    setCounts(cart.item_count);
    itemsEl.replaceChildren();
    dialog.querySelector('[data-gpc-cart-total]').textContent = formatMoney(cart.total_price, cart.currency);
    dialog.querySelector('[data-gpc-cart-foot]').hidden = !cart.item_count;
    if (!cart.item_count) {
      itemsEl.append(safeText('p', translate('cart.empty')));
      const link = safeText('a', translate('ui.explore'), 'gpc-button'); link.href = window.gpcTheme?.productUrl || root + 'collections/all'; itemsEl.append(link);
      return;
    }
    cart.items.forEach(item => {
      const row = document.createElement('div'); row.className = 'gpc-cart-item';
      const image = document.createElement('img'); image.width = 78; image.height = 98;
      const color = window.gpcTheme?.variantColors?.[String(item.variant_id)] || '';
      const colorLabel = color && strings['colors.' + color] ? translate('colors.' + color) : item.variant_title;
      image.src = (String(item.product_id) === String(window.gpcTheme?.productId) && window.gpcTheme?.images?.[color]) || item.featured_image?.url || item.image;
      image.alt = item.product_title;
      if (String(item.product_id) === String(window.gpcTheme?.productId)) image.dataset.gpcFinish = color;
      const details = document.createElement('div'); details.className = 'gpc-cart-item__details';
      const name = [item.product_title, colorLabel].filter(Boolean).join(' — ');
      const remove = safeText('button', translate('cart.remove'), 'gpc-cart-item__remove');
      remove.type = 'button'; remove.dataset.gpcCartRemove = item.key;
      remove.setAttribute('aria-label', translate('cart.remove_label', {name}));
      details.append(safeText('h3', item.product_title), safeText('p', [colorLabel, translate('cart.quantity', {quantity: item.quantity})].filter(Boolean).join(' · ')), safeText('strong', formatMoney(item.final_line_price, cart.currency)), remove);
      row.append(image, details); itemsEl.append(row);
    });
  }
  async function showCart(opener) {
    const dialog = document.getElementById('gpc-cart');
    if (!dialog?.showModal) { window.location.assign(root + 'cart'); return; }
    const request = ++cartRequest;
    cartOpener = opener || document.activeElement;
    if (!dialog.open) dialog.showModal();
    const itemsEl = dialog.querySelector('[data-gpc-cart-items]');
    const foot = dialog.querySelector('[data-gpc-cart-foot]');
    itemsEl.replaceChildren(safeText('p', translate('cart.updating')));
    foot.hidden = true;
    dialog.querySelector('[data-gpc-cart-error]').hidden = true;
    try {
      // A reopened drawer must read the cart after any pending removal finishes.
      if (cartChange) await cartChange.catch(() => {});
      if (request !== cartRequest) return;
      const response = await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Your bag could not be loaded.');
      const cart = await response.json();
      if (request !== cartRequest) return;
      renderCart(cart, dialog);
    } catch (error) {
      if (request !== cartRequest) return;
      itemsEl.replaceChildren(safeText('p', translate('cart.load_error')));
      const link = safeText('a', translate('cart.open_page'), 'gpc-button'); link.href = root + 'cart'; itemsEl.append(link);
    }
  }
  async function removeCartItem(button) {
    if (cartChange) return;
    const dialog = button.closest('#gpc-cart');
    const itemsEl = dialog.querySelector('[data-gpc-cart-items]');
    const foot = dialog.querySelector('[data-gpc-cart-foot]');
    const errorEl = dialog.querySelector('[data-gpc-cart-error]');
    const buttons = [...itemsEl.querySelectorAll('[data-gpc-cart-remove]')];
    const index = buttons.indexOf(button);
    const request = ++cartRequest;
    errorEl.hidden = true; foot.hidden = true;
    itemsEl.setAttribute('aria-busy', 'true');
    buttons.forEach(el => { el.disabled = true; });
    button.textContent = translate('cart.removing');
    // Line keys distinguish items with the same variant but different properties.
    cartChange = fetch(root + 'cart/change.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: button.dataset.gpcCartRemove, quantity: 0 })
    }).then(async response => {
      const cart = await response.json();
      if (!response.ok) throw new Error('The item could not be removed. Please try again.');
      return cart;
    });
    try {
      const cart = await cartChange;
      setCounts(cart.item_count);
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'gpc-cart', cartData: cart });
      }
      if (request !== cartRequest) return;
      const restoreFocus = document.activeElement === button;
      renderCart(cart, dialog);
      if (dialog.open && restoreFocus) {
        const remaining = itemsEl.querySelectorAll('[data-gpc-cart-remove]');
        (remaining[Math.min(index, remaining.length - 1)] || itemsEl.querySelector('a') || dialog.querySelector('[data-gpc-cart-close]')).focus();
      }
    } catch (error) {
      if (request !== cartRequest) return;
      errorEl.textContent = translate('cart.remove_error');
      errorEl.hidden = false; foot.hidden = false;
    } finally {
      cartChange = null;
      itemsEl.removeAttribute('aria-busy');
      buttons.forEach(el => { el.disabled = false; });
      button.textContent = translate('cart.remove');
    }
  }
  document.addEventListener('click', event => {
    const open = event.target.closest('[data-gpc-cart-open]');
    if (open && !event.metaKey && !event.ctrlKey) { event.preventDefault(); showCart(open); }
    if (event.target.closest('[data-gpc-cart-close]')) document.getElementById('gpc-cart')?.close();
    const remove = event.target.closest('[data-gpc-cart-remove]');
    if (remove) removeCartItem(remove);
    const mobileLink = event.target.closest('.gpc-mobile-menu a');
    if (mobileLink) mobileLink.closest('details').open = false;
  });
  document.addEventListener('keydown', event => { if (event.key === 'Escape') document.querySelectorAll('.gpc-mobile-menu[open]').forEach(menu => { menu.open = false; menu.querySelector('summary').focus(); }); });
  const dialog = document.getElementById('gpc-cart');
  dialog?.addEventListener('close', () => cartOpener?.focus());
  dialog?.addEventListener('click', event => { if (event.target === dialog) { const r = dialog.getBoundingClientRect(); if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) dialog.close(); } });

  function initProduct(section) {
    if (section.dataset.gpcReady) return;
    const dataEl = section.querySelector('[data-gpc-product-data]');
    let data;
    try { data = JSON.parse(dataEl.textContent); } catch (_) { return; }
    const form = section.querySelector('form[action*="/cart/add"]');
    const select = section.querySelector('[data-gpc-variant]');
    const quantity = form?.querySelector('[name="quantity"]');
    if (!form || !select || !quantity) return;
    section.dataset.gpcReady = 'true'; section.classList.add('gpc-product-ready');
    let selected = data.variants.find(v => String(v.id) === select.value);
    let busy = false;
    let imageVersion = 0;
    const errorEl = section.querySelector('[data-gpc-error]');
    const setButtons = () => {
      section.querySelectorAll('[data-gpc-add], [data-gpc-buy]').forEach(button => { button.disabled = busy || !selected?.available; });
      section.querySelectorAll('[data-gpc-add-label]').forEach(label => { label.textContent = busy ? translate('product.adding') : selected?.available ? translate('product.add') : selected ? translate('ui.sold_out') : translate('ui.not_available'); });
      section.querySelectorAll('[data-gpc-color], [data-gpc-quantity]').forEach(button => { button.disabled = busy; });
      quantity.disabled = busy;
    };
    function chooseColor(key, updateUrl = true) {
      const color = data.colors.find(c => c.key === key);
      if (!color) return;
      selected = data.variants.find(v => v.color === key);
      section.querySelectorAll('[data-gpc-color]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.gpcColor === key)));
      section.querySelector('[data-gpc-color-label]').textContent = color.label;
      select.value = selected ? String(selected.id) : '';
      select.disabled = !selected;
      section.querySelectorAll('[data-gpc-price]').forEach(el => { el.textContent = selected?.price || translate('ui.not_available'); });
      const compare = section.querySelector('[data-gpc-compare]');
      compare.hidden = !selected?.compare; compare.textContent = selected?.compare || '';
      const status = section.querySelector('[data-gpc-availability]');
      status.textContent = !selected ? translate('product.color_unavailable') : selected.available ? '' : translate('product.color_sold_out');
      quantity.min = selected?.min || 1; quantity.step = selected?.step || 1;
      if (selected?.max) quantity.max = selected.max; else quantity.removeAttribute('max');
      quantity.value = selected?.min || 1;
      const image = section.querySelector('[data-gpc-product-image]');
      image.dataset.gpcFinish = key;
      image.alt = translate('images.product', {color: color.label});
      const version = ++imageVersion;
      // All default finishes share one image. Do not reload it on color changes.
      if (new URL(color.image, location.href).href !== image.src) {
        const preload = new Image();
        preload.onload = () => { if (version === imageVersion) image.src = color.image; };
        preload.src = color.image;
      }
      if (updateUrl) {
        const url = new URL(window.location.href);
        if (selected) { url.searchParams.set('variant', selected.id); url.searchParams.delete('color'); }
        else { url.searchParams.set('color', key); url.searchParams.delete('variant'); }
        history.replaceState({}, '', url);
      }
      errorEl.hidden = true; setButtons();
    }
    section.querySelectorAll('[data-gpc-color]').forEach(button => button.addEventListener('click', () => { if (!busy) chooseColor(button.dataset.gpcColor); }));
    section.querySelectorAll('[data-gpc-quantity]').forEach(button => button.addEventListener('click', () => {
      const increment = Number(quantity.step) || 1, min = Number(quantity.min) || 1;
      let next = Math.max(min, (Number(quantity.value) || min) + Number(button.dataset.gpcQuantity) * increment);
      if (quantity.max) next = Math.min(next, Number(quantity.max));
      quantity.value = next;
    }));
    async function addToCart(checkout) {
      if (busy || !selected?.available) return;
      if (!form.reportValidity()) return;
      errorEl.hidden = true;
      const body = { items: [{ id: selected.id, quantity: Number(quantity.value) }] };
      busy = true; setButtons();
      try {
        const response = await fetch(root + 'cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || result.message || translate('product.add_error'));
        if (checkout) { window.location.assign(root + 'checkout'); return; }
        await showCart(section.querySelector('[data-gpc-add]'));
      } catch (error) { errorEl.textContent = document.documentElement.lang === 'ar' ? translate('product.add_error') : error.message || translate('product.add_error'); errorEl.hidden = false; }
      finally { busy = false; setButtons(); }
    }
    form.addEventListener('submit', event => { event.preventDefault(); addToCart(false); });
    section.querySelector('[data-gpc-buy]')?.addEventListener('click', () => addToCart(true));
    const urlColor = new URL(window.location.href).searchParams.get('color');
    chooseColor(urlColor || selected?.color || data.colors[0]?.key, false);
    const sticky = section.querySelector('.gpc-sticky-buy');
    if ('IntersectionObserver' in window && sticky) {
      new IntersectionObserver(entries => { const entry = entries[0]; sticky.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.bottom < 0); }, { threshold: 0 }).observe(form);
    }
  }
  const init = scope => { initMotion(scope); scope.querySelectorAll('[data-gpc-product]').forEach(initProduct); };
  init(document);
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('[data-gpc-parallax]').forEach(el => { activeScenes.delete(el); sceneObserver?.unobserve(el); });
  });
})();
